import React, { useState, useMemo, useEffect } from "react";
import {
  Box,
  Typography,
  Paper,
  Stack,
  alpha,
  useTheme,
  Fade,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Button,
} from "@mui/material";
import CheckCircleOutlinedIcon from "@mui/icons-material/CheckCircleOutlined";
import AccountBalanceOutlinedIcon from "@mui/icons-material/AccountBalanceOutlined";
import CalculateOutlinedIcon from "@mui/icons-material/CalculateOutlined";
import MainCompFtcBreakdown from "./MainCompFtcBreakdown";
import FTCCalculator from "./calculator/FtcCalculator";
const { buildMainComputation } = require("./calculation");

const FtcForm = ({
  companyType = "common",
  foreignIncomeData,
  breakdownRows,
  onBreakdownChange,
  onRecordData,
  canEdit = true,
}) => {
  const STORAGE_KEY = "ftcForm_activeStep";
  const theme = useTheme();
  const [activeStep, setActiveStep] = useState(() => {
    const savedStep = localStorage.getItem(STORAGE_KEY);
    return savedStep !== null ? parseInt(savedStep, 10) : 0;
  });
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, activeStep.toString());
  }, [activeStep]);

  // Calculate main computation data
  const mainComputation = useMemo(() => {
    return buildMainComputation(foreignIncomeData, breakdownRows, companyType);
  }, [foreignIncomeData, breakdownRows]);

  // Check if we have locked foreign income data
  const hasLockedData = foreignIncomeData?.some((row) => row.isLocked);
  const hasValidBreakdown =
    breakdownRows.totalRevenue >= 0 && breakdownRows.taxPayable >= 0;

  // Step validation
  const isStepValid = (step) => {
    switch (step) {
      case 0:
        return hasValidBreakdown;
      case 1:
        return hasLockedData && hasValidBreakdown;
      default:
        return false;
    }
  };

  // Automatically show second step when it becomes valid
  useEffect(() => {
    if (isStepValid(1)) {
      setActiveStep(1);
    }
  }, [hasLockedData, hasValidBreakdown]);

  const handleBreakdownChange = (value, { field }) => {
    onBreakdownChange?.(value, { field });
  };

  const handleNext = () => {
    if (activeStep < 1) {
      setActiveStep((prev) => prev + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  const handleStepClick = (step) => {
    if (step <= activeStep || isStepValid(step)) {
      localStorage.setItem(STORAGE_KEY, step);
      setActiveStep(step);
    }
  };

  const steps = [
    {
      label: "Main Computation Breakdown",
      description: "Enter main computation data for FTC calculation",
      icon: <CalculateOutlinedIcon />,
      content: (
        <Box sx={{ p: 1 }}>
          <MainCompFtcBreakdown
            data={mainComputation}
            breakdownRows={breakdownRows}
            onChange={handleBreakdownChange}
            canEdit={canEdit}
          />
        </Box>
      ),
    },
    {
      label: "FTC Calculation Results",
      description: "Review and finalize your FTC calculation",
      icon: <CheckCircleOutlinedIcon />,
      content: (
        <Box sx={{ p: 1 }}>
          <FTCCalculator
            foreignIncomeData={foreignIncomeData}
            mainComputationData={mainComputation}
            onRecordData={onRecordData}
          />
        </Box>
      ),
    },
  ];

  return (
    <Box sx={{ width: "100%" }}>
      {/* Form Header */}
      <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 3,
          borderRadius: 3,
          background: `linear-gradient(135deg, ${alpha(
            theme.palette.primary.main,
            0.08
          )}, ${alpha(theme.palette.primary.main, 0.04)})`,
          border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
          position: "relative",
          overflow: "hidden",
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: 3,
            background: `linear-gradient(90deg, ${
              theme.palette.primary.main
            }, ${alpha(theme.palette.primary.main, 0.6)})`,
          },
        }}
      >
        <Stack direction='row' spacing={2} alignItems='center'>
          <Box
            sx={{
              width: 48,
              height: 48,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: 2,
              bgcolor: (theme) => alpha(theme.palette.primary.main, 0.12),
              flexShrink: 0,
            }}
          >
            <AccountBalanceOutlinedIcon
              sx={{
                fontSize: 24,
                color: "primary.main",
              }}
            />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography
              variant='h6'
              sx={{
                fontWeight: 600,
                color: "text.primary",
                mb: 0.5,
              }}
            >
              Foreign Tax Credit Calculation
            </Typography>
            <Typography
              variant='body2'
              sx={{
                color: "text.secondary",
                fontSize: "0.85rem",
              }}
            >
              Complete the steps below to calculate your foreign tax credit
              claim
            </Typography>
          </Box>
          <Box sx={{ textAlign: "right" }}>
            <Typography
              variant='caption'
              sx={{
                color: "text.secondary",
                fontSize: "0.75rem",
              }}
            >
              Step {activeStep + 1} of {steps.length}
            </Typography>
            <Typography
              variant='h6'
              sx={{
                fontWeight: 600,
                color: "primary.main",
              }}
            >
              {Math.round(((activeStep + 1) / steps.length) * 100)}%
            </Typography>
          </Box>
        </Stack>
      </Paper>

      {/* Stepper */}
      <Paper
        elevation={0}
        sx={{
          p: 3,
          borderRadius: 3,
          overflow: "hidden",
          border: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
          background: `linear-gradient(145deg, ${
            theme.palette.background.paper
          }, ${alpha(theme.palette.primary.main, 0.01)})`,
        }}
      >
        <Stepper activeStep={activeStep} orientation='vertical'>
          {steps.map((step, index) => (
            <Step
              key={step.label}
              sx={{
                "& .MuiStepLabel-root": {
                  cursor:
                    isStepValid(index) || index <= activeStep
                      ? "pointer"
                      : "default",
                },
              }}
            >
              <StepLabel
                onClick={() => handleStepClick(index)}
                icon={
                  <Box
                    sx={{
                      width: 32,
                      height: 32,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      borderRadius: "50%",
                      bgcolor: (theme) => {
                        if (index < activeStep)
                          return theme.palette.success.main;
                        if (index === activeStep)
                          return theme.palette.primary.main;
                        return alpha(theme.palette.grey[400], 0.3);
                      },
                      color: (theme) => {
                        if (index < activeStep)
                          return theme.palette.success.contrastText;
                        if (index === activeStep)
                          return theme.palette.primary.contrastText;
                        return theme.palette.text.disabled;
                      },
                      transition: "all 0.2s ease-in-out",
                    }}
                  >
                    {index < activeStep ? (
                      <CheckCircleOutlinedIcon sx={{ fontSize: 18 }} />
                    ) : (
                      React.cloneElement(step.icon, { sx: { fontSize: 18 } })
                    )}
                  </Box>
                }
                sx={{
                  "& .MuiStepLabel-label": {
                    fontWeight: index === activeStep ? 600 : 500,
                    color:
                      index === activeStep ? "primary.main" : "text.primary",
                  },
                  "& .MuiStepLabel-labelContainer": {
                    "& .MuiStepLabel-label": {
                      fontSize: "0.95rem",
                    },
                  },
                }}
              >
                <Box>
                  <Typography
                    variant='subtitle1'
                    sx={{
                      fontWeight: index === activeStep ? 600 : 500,
                      color:
                        index === activeStep ? "primary.main" : "text.primary",
                    }}
                  >
                    {step.label}
                  </Typography>
                  <Typography
                    variant='body2'
                    sx={{
                      color: "text.secondary",
                      fontSize: "0.8rem",
                      mt: 0.5,
                    }}
                  >
                    {step.description}
                  </Typography>
                </Box>
              </StepLabel>
              <StepContent>
                <Fade in={index === activeStep} timeout={300}>
                  <Box>{step.content}</Box>
                </Fade>
                <Box sx={{ px: 1, py: 2 }}>
                  <Stack direction='row' spacing={2}>
                    <Button
                      disabled={activeStep === 0}
                      onClick={handleBack}
                      variant='outlined'
                      size='small'
                    >
                      Back
                    </Button>
                    {activeStep < steps.length - 1 && (
                      <Button
                        variant='contained'
                        onClick={handleNext}
                        disabled={!isStepValid(activeStep + 1)}
                        size='small'
                      >
                        Next
                      </Button>
                    )}
                  </Stack>
                </Box>
              </StepContent>
            </Step>
          ))}
        </Stepper>
      </Paper>
    </Box>
  );
};

export default FtcForm;
